/**
 * Created by rickborst on 24/04/2017.
 */

import _ from 'lodash';

import {
    convertForFetchViews,
    convertFromFetchViews,
    convertAssetToCollectionName,
    convertAssetToDiscoverName
} from '../adapters/views';
import {isValidFetchResponse} from '../validators/views';
import {prepareRails} from './rails';
import {InvalidResponse} from 'errors/api/invalidresponse';

import {ImageRatios} from 'models/image';
import {default as View, ViewNames} from 'models/view';

import {getInstance as getStore} from 'store/store';
import {
    ViewStatuses,
    setView
} from 'store/actions/viewtree';

import {RailTypes} from 'models/rail';
import {getInstance as getDataSources} from 'datasources';

/**
 * Returns the current view tree.
 *
 * @returns {Object} View tree.
 */
function getViewTree () {
    let store = getStore(),
        state = store.getState();

    return state.viewTree;
}

/**
 * Returns a view by id from the store.
 *
 * @param {string} id - View id.
 * @param {Object} views - View map.
 * @returns {Object|undefined} View or undefined.
 */
function getViewFromViewMapById (id, views = getViewTree().views) {

    return _.find(views, view => {

        return view.id === id;
    });
}

/**
 * Returns a view by name from the store.
 *
 * @param {string} name - View name.
 * @param {Object} views - View map.
 * @returns {Object|undefined} View or undefined.
 */
function getViewFromViewMapByName (name, views = getViewTree().views) {

    return _.find(views, view => {

        if (view.name === name || view.id === name) {
            return view;
        }
        
    });
}

/**
 * Prepares a view by loading its contents.
 *
 * @param {Object} view - View to be prepared.
 * @returns {Promise} Prepare view promise.
 */
function prepareView (view) {
    if (_.get(view, 'status') === ViewStatuses.PREPARED) {
        return Promise.resolve(view);
    }

    return prepareRails.call(this, view.modules)
        .then((rails) => {
            view.modules = rails;
            view.status = ViewStatuses.PREPARED;

            return view;
        });
}

/**
 * Returns multiple views based on an optional id.
 *
 * @param {string|int} [id] Optional id.
 * @returns {Promise} Fetch views promise.
 */
export function fetchViews (id) {
    const url = this._baseUrl + this._endPoints.category,
        data = convertForFetchViews.call(this, id);

    return this.read(
        {
            url: url,
            data: data
        })
        .then(response => {
            if (!isValidFetchResponse(response)) {
                throw new InvalidResponse(response);
            }

            return _.map(convertFromFetchViews(response),
                (viewData) => new View(viewData));
        });
}

/**
 * Returns a single view by id.
 *
 * @param {string|int} id - View id.
 * @param {any} params - View params.
 * @returns {Promise} Get view promise.
 */
export function getView (id, params) {
    let view = getViewFromViewMapById(id),
        store = getStore();

    if (view) {

        if (view.status === ViewStatuses.PREPARED) {

            return Promise.resolve(view);
        }

        // Make sure we don't edit the object in the store directly.
        view = {...view};

        return prepareView.call(this, view, params)
            .then(() => {
                store.dispatch(setView(view));
            });
    }

    return fetchViews.call(this)
        .then(results => {
            let needle = _.find(results || [], ['id', id]);

            _.forEach(results || [], (result) => {

                if (result !== needle) {
                    store.dispatch(setView(result));
                }
            });
            return needle;
        })
        .then(prepareView.bind(this));
}

/**
 * Returns a single view by name.
 *
 * @param {string} name - View name.
 * @param {any} params - View params.
 * @returns {Promise} Get view promise.
 */
export function getViewByName (name, params) {
    let view = getViewFromViewMapByName.call(this, name, params),
        store = getStore(),
        dataSources = getDataSources(),
        viewFromMenu = null;

    return dataSources.getConfig().then((config) => {
        let {catchupCategoryID, discoverCategoryID} = _.get(config, ['_presets']);

        return {
            catchupCategoryID,
            discoverCategoryID
        };
    }).then((configIDs) => {
        switch (name) {
            case 'discover':
                if (params.name === 'discover-sport-5') {
                    viewFromMenu = configIDs.discoverCategoryID;
                }
                break;
            case 'catch-up':
                viewFromMenu = configIDs.catchupCategoryID;
                break;

            // TO DO: Add all pages ids and make it global (discuss with Josef).
        }

        // View already exists
        if (view) {
            /* eslint-disable no-use-before-define */
            switch (view.name) {

                case ViewNames.COLLECTION:

                    return getCollection.call(this, view, params);

                case ViewNames.DISCOVER:
                    if (params.name !== 'discover-sport-5') {
                        return getDiscover.call(this, view, params);
                    }
            }

            /* eslint-enable no-use-before-define */

            // View has been prepared
            if (view.status === ViewStatuses.PREPARED) {

                return Promise.resolve(view);
            }

            // Make sure we don't edit the object in the store directly.
            view = {...view};

            return prepareView.call(this, view, params)
                .then(() => {

                    store.dispatch(setView(view));

                    return Promise.resolve(view);
                });
        }

        // View does not exist in the store yet
        return fetchViews.call(this)
            .then(results => {

                let needle = {},
                    nestedViews,
                    rootCategoryOfNestedViews;

                if (!viewFromMenu) {
                    needle = _.find(results || [], ['name', name]);

                   if (!needle) {

                       // try to search in discover categories
                       rootCategoryOfNestedViews = _.find(results || [], ['id', configIDs.discoverCategoryID]);
                       nestedViews = _.get(rootCategoryOfNestedViews, ['views']);
                       needle = _.find(nestedViews || [], ['name', params.name]);
                   }
                } else {
                    needle = _.find(results || [], ['id', viewFromMenu]);

                    if (!needle) {

                        // loop nested views
                        rootCategoryOfNestedViews = _.find(results || [], ['name', name]);
                        nestedViews = _.get(rootCategoryOfNestedViews, ['views']);
                        needle = _.find(nestedViews || [], ['id', viewFromMenu]);
                    }
                }

                _.forEach(results || [], (result) => {

                    if (result !== needle) {
                        store.dispatch(setView(result));
                    }
                });

                return needle;
            })
            .then(needle => {
                return prepareView.call(this, needle, params);
            })
            .then(result => {

                store.dispatch(setView(result));

                /* eslint-disable no-use-before-define */
                switch (result.name) {

                    case ViewNames.COLLECTION:

                        return getCollection.call(this, result, params);

                    case ViewNames.DISCOVER:
                        if (params.name !== 'discover-sport-5') {
                            return getDiscover.call(this, result, params);
                        }
                }
                /* eslint-enable no-use-before-define */

                return Promise.resolve(result);
            });


    });
}

/**
 * Function specific from getting collection views.
 *
 * @param {Object} collectionView - Mother collection view.
 * @param {Object} params - Collection view params.
 * @returns {Promise} Get collection promise.
 */
function getCollection (collectionView, params = {}) {
    let store = getStore(),
        asset,
        miniEpg,
        getMiniEpgView,
        collectionViewPromise;

    collectionViewPromise = collectionView.status !== ViewStatuses.PREPARED
        ? prepareView.call(this, collectionView)
            .then(() => {

                store.dispatch(setView(collectionView));
            })
        : Promise.resolve(collectionView);

    // We need the MiniEPG in the collection page
    getMiniEpgView = getViewByName.call(this, ViewNames.MINI_EPG);

    return getMiniEpgView
        .then((result) => {
            miniEpg = result;
            miniEpg.type = RailTypes.MINI_EPG;
            return miniEpg;
        })
        .then(collectionViewPromise)
        .then(this._dataSources.getAsset.bind(this, params.assetId, params.query))
        .catch((error) => {
            return Promise.reject(error);
        })
        .then((loadedAsset)=>{
            let {id: loadedAssetId} = loadedAsset;

            return this._dataSources.getReminder(loadedAssetId).then((reminder) => {

                loadedAsset.setReminder(reminder);
                return loadedAsset;
            }).catch(() => {
                return loadedAsset;
            });
        })
        .then(result => {
            asset = result;
            return convertAssetToCollectionName.call(this, asset);
        })
        .then(collectionName => {
            let collection = getViewFromViewMapByName.call(
                this,
                collectionName,
                collectionView.views);

            if (!collection) {

                collectionView.modules = [
                    asset,
                    miniEpg
                ];

                return Promise.resolve(collectionView);
            }

            return prepareView.call(this, collection)
                .then(result => {

                    store.dispatch(setView(result));

                    return {
                        ...result,
                        modules: [
                            asset,
                            miniEpg,
                            ...result.modules
                        ]
                    };
                });
        });
}

/**
 * Returns a discover name based on an asset.
 *
 * @param {Object} asset - Asset object.
 * @returns {Promise} Discover name promise.
 */
export function getDiscoverNameByAsset (asset) {

    return Promise.resolve(convertAssetToDiscoverName.call(this, asset));
}

/**
 * Function specific from getting collection views.
 *
 * @param {Object} discoverView - Mother collection view.
 * @param {Object} params - Discover view params.
 * @returns {Promise} Get discover promise.
 */
function getDiscover (discoverView, params = {}) {
    let store = getStore(),
        discoverViewPromise;

    discoverViewPromise = discoverView.status !== ViewStatuses.PREPARED
        ? prepareView.call(this, discoverView)
            .then(() => {

                store.dispatch(setView(discoverView));
            })
        : Promise.resolve(discoverView);

    return discoverViewPromise
        .then(() => {
            let discoverName = params.name || '',
                discoverChildView = getViewFromViewMapByName.call(
                    this,
                    discoverName,
                    discoverView.views);

            if (!discoverChildView) {

                discoverView.modules = [];

                return Promise.resolve(discoverView);
            }

            return prepareView.call(this, discoverChildView)
                .then(result => {

                    store.dispatch(setView(result));

                    return result;
                });
        });
}

/**
 * Returns the background of a given view.
 *
 * @param {Object} view - View object.
 * @returns {Promise} View background promise.
 */
export function getViewBackground (view) {
    let image = _.find(view.images, entry => {

        return entry.ratio === ImageRatios['16:9'];
    });

    if (image) {
        return Promise.resolve(image);
    }

    // Get the first 16:9 image from the assets in the view.
    for (let i = 0, l = view.modules.length; i < l; i++) {
        let module;

        module = view.modules[i];

        for (let ii = 0, ll = module.assets.length; ii < ll; ii++) {
            let asset = module.assets[ii];

            image = _.find(asset.images, entry => {

                return entry.ratio === ImageRatios['16:9'];
            });

            if (image) {
                return Promise.resolve(image);
            }
        }
    }

    return Promise.resolve(null);
}


// WEBPACK FOOTER //
// ./src/js/datasources/kaltura/services/views.js